<script context="module" lang="ts">
    export type ZoneFromCoordsParams = {
        vehicleIds: number[],
        limit: number,
        radius: number,
        day: string,
        distanceFrom: string|undefined,
        multi: boolean
    };
</script>

<script lang="ts">
    import { closeModal } from 'svelte-modals';
    import { _ } from 'svelte-i18n';
    import { get } from 'svelte/store';
    import ButtonMain from '../../shared/ButtonMain.svelte';
    import ButtonBack from '../../shared/ButtonBack.svelte';
    import SelectDay from '../../shared/SelectDay.svelte';
    import { planning, debug } from '../../../stores/store';
    import { api, clientSettings } from '../../../stores/api';
    import Spinner from '../../shared/Spinner/Spinner.svelte';
    import Select from '../../shared/Select.svelte';
    import DualList from '../../shared/DualList.svelte';
    import type { DualListItem } from '../../shared/DualList.svelte';
    import { onMount } from 'svelte';
    import { VehiclesApi } from '../Vehicles/VehiclesApi';
    import TelematicsSliderSingle from '../TelematicsSlider/TelematicsSliderSingle.svelte';
    import RadioButton from '../../shared/RadioButton.svelte';
    import VehicleList from '../Vehicles/VehicleList.svelte';

    export let isOpen: boolean = true;
    export let error: string = '';

    export let onProceed: (params: any) => Promise<boolean|string> = async function(params: ZoneFromCoordsParams) { return true };
    export let onCancel: () => void = () => {};

    export let limit: string = '100';
    export let radius: string = '100';
    export let day: string = get(planning).selectedDay;
    export let distanceFrom: string = 'nearest';
    export let alreadyUsedVehicles: number[];
    export let multi: boolean = false;
    export let paramsOnly: boolean = false;

    let busy: boolean = false;
    let vehicles: DualListItem[] = [];
    let selectedVehicleIds: number[] = multi ? [] : [0];

    function onKeyUp(ev: any) {
        if (ev.key == 'Enter') {
            proceed();
        }
    }

    async function proceed() {
        if (limit && radius && day.match(/^\d{4}-\d{2}-\d{2}$/)) {
            busy = true;
            let proceed = await onProceed({vehicleIds: selectedVehicleIds, limit: parseInt(limit), radius: parseInt(radius), day, distanceFrom, multi})
            if (proceed === true) {
                closeModal();
            } else if (typeof(proceed) == 'string') {
                error = proceed;
                busy = false;
            }
        }
    }

    onMount(() => {
        busy = true;
        VehiclesApi.getAll(get(api)).then((response) => {
            for (let vehicle of response) {
                if (vehicle.id && !alreadyUsedVehicles.includes(vehicle.id)) {
                    vehicles.push({id: vehicle.id?.toString(), label: vehicle.name, selected: false});
                }
            }
            vehicles.push({id: '', label: 'brak pojazdu', selected: false, multi: true});
            busy = false;
        });
    });


    $: {
        if (multi) {
            selectedVehicleIds = vehicles.filter((v) => v.selected).sort((va, vb) => (va.order || 0) - (vb.order || 0)).map((v) => parseInt(v.id || '0'));
        }
        limit = limit;
        radius = radius;
    }
</script>

{#if isOpen}
    <div role="dialog" class="modal">
        <div class="contents">
            {#if busy}
                <div style="display: flex; width: 100%; height: 100%; align-items: center; justify-contents: center;">
                    <Spinner/>
                </div>
            {:else}
                {#if error != ''}
                    <div class="error">{@html error}</div>
                {/if}
                {#if !paramsOnly}
                    <h3 style="text-align: center; margin-top: 0;">{$_('zone-from-coords-choose-vehicle')}</h3>

                    <div class="mode-selector">
                        <RadioButton label="{$_('zone-from-coords-mode-single')}" checked={!multi} onSet={() => {
                            selectedVehicleIds = [0]
                            multi = false;
                        }} scale={0.7}/>
                        <RadioButton label="{$_('zone-from-coords-mode-multiple')}" checked={multi} onSet={() => {
                            selectedVehicleIds = [];
                            multi = true;
                        }} scale={0.7}/>
                    </div>

                    {#if multi}
                        <DualList bind:items={vehicles} sortable={true}/>
                    {:else}
                        <div class="vehicle-list">
                            <VehicleList
                                {alreadyUsedVehicles}
                                allowUseAlreadyUsedVehicle={false}
                                addMode={false}
                                letAddVehicle={false}
                                compactMode={true}
                                onlyNotUsed={true}
                                activeOnly={true}
                                onSelect={(vehicleId) => {
                                    if (typeof(vehicleId) == 'number') {
                                        selectedVehicleIds = [vehicleId];
                                    }
                                }}
                                writableMode={false}
                                selectMode="radio"
                                selectedVehicles={[0]}
                                unSelected={true}
                            />
                        </div>
                    {/if}
                {/if}

                <h3 style="text-align: center; margin-bottom: 5px">{$_('zone-from-coords-set-parameters')}</h3>

                <div class="zone-param">
                    <span>{$_('zone-from-coords-jobs-limit')}</span>
                    <div class="slider">
                        <TelematicsSliderSingle props={{min: 1, max: 100, currentValue: limit, unit: '', roundValues: false, sliderWidth: 300}} onChange={(val) => { limit = Math.round(val).toString(); }}/>
                    </div>
                </div>
                <div class="zone-param">
                    <span>{$_('zone-from-coords-radius')}</span>
                    <div class="slider">
                        <TelematicsSliderSingle props={{min: 1, max: 100, currentValue: radius, unit: '', roundValues: false, sliderWidth: 300}} onChange={(val) => { radius = Math.round(val).toString(); }}/>
                    </div>
                </div>
                <div class="zone-param">
                    <div class="control">
                        <span>{$_('zone-from-coords-day')}</span>
                        <SelectDay
                            bind:day={day}
                            autoApply={true}
                            showRelativeToday={$clientSettings.display.showRelativeToday}
                            drops="up"
                        />
                    </div>
                </div>
                {#if $debug}
                    <div class="zone-param">
                        <div class="control">
                            <span style="width: calc(100% - 180px)">{$_('distance_from_label')}</span>
                            <Select items={[{id: 'nearest', label: $_('nearest')}, {id: 'centroid', label: $_('centroid')}, {id: 'start', label: $_('start_point')}]} bind:selected={distanceFrom}/>
                        </div>
                    </div>
                {/if}

                <div class="actions">
                    <ButtonBack
                        text={$_('Cancel')}
                        on:click={() => {
                            closeModal();
                            onCancel();
                        }}
                    />
                    <ButtonMain
                        text={$_('OK')}
                        disabled={((!paramsOnly && selectedVehicleIds.length === 0) || !limit || !limit.match(/^\d+$/) || !radius || !day.match(/^\d{4}-\d{2}-\d{2}$/))}
                        on:click={proceed}
                    />
                </div>
            {/if}
        </div>
    </div>
{/if}

<style>
    .modal {
        position: fixed;
        top: 0;
        bottom: 0;
        right: 0;
        left: 0;
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 1002;

        /* allow click-through to backdrop */
        pointer-events: none;
    }

    .contents {
        width: 500px;
        border-radius: 6px;
        background: white;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        pointer-events: auto;
        font-size: 14px;
        padding: 30px;
        border: 3px solid var(--automapa-blue);
        box-shadow:
            0 0 0 10px #1c5dc722,
            0 4px 8px rgba(0, 0, 0, 0.2);
    }

    .zone-param {
        margin-top: 10px;
        display: flex;
        flex-direction: column;
        align-items: center;
    }

    .slider {
        margin: 5px 0;
        width: 100%;
        padding-left: 15px;
    }

    .control {
        display: flex;
        align-items: center;
    }

    .actions {
        margin-top: 32px;
        display: flex;
        justify-content: center;
        column-gap: 10px;
    }

    .error {
        color: var(--automapa-red);
        margin-bottom: 10px;
    }

    .mode-selector {
        display: flex;
        justify-content: space-evenly;
        margin-bottom: 10px;
    }

    .vehicle-list {
        overflow-y: auto;
        overflow-x: hidden;
        max-height: 300px;
        background-color: white;
        padding: 20px 0;
        border-radius: 3px;
        flex: 1 1 auto;
        /*overflow: hidden;*/
    }
</style>
